// =============================================
// Unsaved-changes guard — app-styled Save / Discard / Cancel.
// =============================================
// Replaces the old native window.confirm with an in-app modal (see
// src/components/ui/UnsavedChangesModal.tsx, mounted once globally).
//
// Flow: a form marks itself dirty (setFormDirty) and optionally registers how to
// save itself (registerSaveHandler). Any navigation/tab handler calls
// `await confirmDiscardChanges()` before navigating:
//   - not dirty                 → resolves true immediately (no modal)
//   - dirty → modal opens:
//       Save    → runs the registered save handler; true if it succeeds
//       Discard → drops changes; true
//       Cancel  → false (navigation aborts; stay put)
// "Save" only appears when a save handler is registered for the current form.
// =============================================

export type UnsavedChoice = 'save' | 'discard' | 'cancel';

let _dirty = false;
// The current dirty form's save fn — returns true on success (false = validation/error → stay).
let _saveHandler: (() => boolean | Promise<boolean>) | null = null;

interface PendingRequest {
  message: string;
  canSave: boolean;
  resolve: (choice: UnsavedChoice) => void;
}
let _pending: PendingRequest | null = null;

const _listeners = new Set<() => void>();
function emit() { _listeners.forEach(l => l()); }

/** Subscribe the modal component to pending-request changes. Returns an unsubscribe. */
export function subscribeUnsaved(listener: () => void): () => void {
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
}
/** The modal reads this to know whether/what to render. */
export function getPendingUnsaved(): PendingRequest | null { return _pending; }
/** The modal calls this when the user picks a button. */
export function resolveUnsaved(choice: UnsavedChoice): void { _pending?.resolve(choice); }

export function setFormDirty(dirty: boolean): void { _dirty = dirty; }
export function isFormDirty(): boolean { return _dirty; }

/** A form registers how to save itself (called when "Save" is clicked). Pass null to clear. */
export function registerSaveHandler(fn: (() => boolean | Promise<boolean>) | null): void {
  _saveHandler = fn;
}

/**
 * Build a modal save handler from a form's existing submit fn (FormEvent → void/Promise).
 * Relies on the sitewide rule that a successful submit clears the dirty flag — so if the
 * flag is still set after running, the submit failed validation (→ false = abort the nav).
 * Lets any form wire "Save & continue" without refactoring its submit's internal returns.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function submitSaver(submit: (e: any) => unknown): () => Promise<boolean> {
  return async () => {
    await Promise.resolve(submit({ preventDefault() {} }));
    return !_dirty;
  };
}

/**
 * Guard a navigation. Resolves true if it's safe to proceed (not dirty, saved, or
 * discarded), false if the user cancelled. Awaits the in-app modal when dirty.
 */
export async function confirmDiscardChanges(message?: string): Promise<boolean> {
  if (!_dirty) return true;
  if (_pending) return false; // a prompt is already open — ignore the new nav

  const choice = await new Promise<UnsavedChoice>(resolve => {
    _pending = {
      message: message || 'You have unsaved changes.',
      canSave: !!_saveHandler,
      resolve,
    };
    emit();
  });
  _pending = null;
  emit();

  if (choice === 'cancel') return false;
  if (choice === 'discard') { _dirty = false; return true; }
  // 'save'
  try {
    const ok = await Promise.resolve(_saveHandler ? _saveHandler() : true);
    if (ok) { _dirty = false; return true; }
    return false; // save failed / validation error → stay so the user can fix it
  } catch {
    return false;
  }
}
